import { ReactElement, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, DoorOpen, AlertTriangle, FolderKanban, ScrollText, Building2, MapPin, Wrench, Loader2, ClipboardList, Receipt, FileSignature, Package } from 'lucide-react';
import { sucheApi, SucheEntitaet, SucheErgebnis, SucheTreffer } from '../services/api';

/**
 * Plattformweite Suche (PLAN_V4_SAAS_ROADMAP Phase 10, Punkt 6).
 *
 * Bis hierher gab es nur seiteneigene Filter -- diese Palette ist von
 * ueberall in der Oberflaeche per Strg+K/Cmd+K erreichbar und durchsucht
 * mehrere Entitaeten auf einmal. Das Backend gruppiert und filtert nach
 * Recht bereits (siehe searchService.ts) -- eine leere Gruppe hier
 * bedeutet entweder "kein Treffer" oder "kein Leserecht", die Palette
 * unterscheidet das bewusst nicht.
 */

const GRUPPEN: Array<{ key: SucheEntitaet; label: string; icon: ReactElement }> = [
  { key: 'ELEMENTE', label: 'Elemente', icon: <DoorOpen size={16} /> },
  { key: 'MAENGEL', label: 'Mängel', icon: <AlertTriangle size={16} /> },
  { key: 'PROJEKTE', label: 'Projekte', icon: <FolderKanban size={16} /> },
  { key: 'GUTACHTEN', label: 'Gutachten', icon: <ScrollText size={16} /> },
  { key: 'KUNDEN', label: 'Kunden', icon: <Building2 size={16} /> },
  { key: 'STANDORTE', label: 'Standorte', icon: <MapPin size={16} /> },
  { key: 'WARTUNGEN', label: 'Wartungen', icon: <Wrench size={16} /> },
  { key: 'ARBEITSAUFTRAEGE', label: 'Arbeitsaufträge', icon: <ClipboardList size={16} /> },
  { key: 'RECHNUNGEN', label: 'Rechnungen', icon: <Receipt size={16} /> },
  { key: 'ANGEBOTE', label: 'Angebote', icon: <FileSignature size={16} /> },
  { key: 'PRODUKTKATALOG', label: 'Produktkatalog', icon: <Package size={16} /> },
];

function zielPfad(treffer: SucheTreffer): string {
  switch (treffer.entitaet) {
    case 'ELEMENTE':
      return `/uebersicht?element=${treffer.id}`;
    case 'MAENGEL':
      return `/maengel?mangel=${treffer.id}`;
    case 'PROJEKTE':
      return `/projekte?projekt=${treffer.id}`;
    case 'GUTACHTEN':
      return `/gutachten/${treffer.id}`;
    case 'KUNDEN':
      return `/einstellungen/kunden?kunde=${treffer.id}`;
    case 'STANDORTE':
      return `/einstellungen/standorte?standort=${treffer.id}`;
    case 'WARTUNGEN':
      return `/wartungen`;
    case 'ARBEITSAUFTRAEGE':
      return `/arbeitsauftraege`;
    case 'RECHNUNGEN':
      return `/rechnungen/${treffer.id}`;
    case 'ANGEBOTE':
      return `/angebote/${treffer.id}`;
    case 'PRODUKTKATALOG':
      return `/produktkatalog`;
  }
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export default function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const [begriff, setBegriff] = useState('');
  const [ergebnis, setErgebnis] = useState<SucheErgebnis | null>(null);
  const [laedt, setLaedt] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Beim Oeffnen: Eingabe leeren und den Fokus auf das Feld legen.
  useEffect(() => {
    if (open) {
      setBegriff('');
      setErgebnis(null);
      const timer = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Entprellte Suche -- erst ab zwei Zeichen, wie am Backend
  // (searchController.ts) gespiegelt, sonst waere jeder erste Tastendruck
  // eine Anfrage ueber den gesamten Bestand.
  useEffect(() => {
    if (!open) return;
    const trimmed = begriff.trim();
    if (trimmed.length < 2) {
      setErgebnis(null);
      setLaedt(false);
      return;
    }
    setLaedt(true);
    const timer = setTimeout(() => {
      sucheApi
        .suchen(trimmed)
        .then(setErgebnis)
        .catch(() => setErgebnis(null))
        .finally(() => setLaedt(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [begriff, open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  const gruppenMitTreffern = GRUPPEN.map((g) => ({
    ...g,
    treffer: ergebnis?.[g.key] ?? [],
  })).filter((g) => g.treffer.length > 0);

  const gehe = (treffer: SucheTreffer) => {
    navigate(zielPfad(treffer));
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-[12vh]"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl rounded-xl bg-white shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-secondary-200 px-4 py-3">
          <Search size={18} className="text-secondary-400 shrink-0" />
          <input
            ref={inputRef}
            value={begriff}
            onChange={(e) => setBegriff(e.target.value)}
            placeholder="Türnummer, Kunde, Mangel, Gutachten..."
            className="flex-1 outline-none text-sm placeholder:text-secondary-400"
          />
          {laedt && <Loader2 size={16} className="animate-spin text-secondary-400 shrink-0" />}
          <kbd className="hidden sm:inline text-[10px] font-mono text-secondary-400 border border-secondary-200 rounded px-1.5 py-0.5">
            Esc
          </kbd>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {begriff.trim().length < 2 && (
            <p className="px-4 py-6 text-sm text-secondary-500 text-center">
              Mindestens zwei Zeichen eingeben.
            </p>
          )}

          {begriff.trim().length >= 2 && !laedt && gruppenMitTreffern.length === 0 && (
            <p className="px-4 py-6 text-sm text-secondary-500 text-center">
              Keine Treffer für „{begriff.trim()}".
            </p>
          )}

          {gruppenMitTreffern.map((gruppe) => (
            <div key={gruppe.key} className="py-2">
              <div className="px-4 pb-1 text-[11px] font-semibold uppercase tracking-wide text-secondary-400">
                {gruppe.label}
              </div>
              {gruppe.treffer.map((treffer) => (
                <button
                  key={`${treffer.entitaet}-${treffer.id}`}
                  onClick={() => gehe(treffer)}
                  className="w-full flex items-center gap-2.5 px-4 py-2 text-left hover:bg-secondary-50 transition-colors"
                >
                  <span className="text-secondary-400 shrink-0">{gruppe.icon}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm text-secondary-900 truncate">{treffer.titel}</span>
                    {treffer.kontext && (
                      <span className="block text-xs text-secondary-500 truncate">{treffer.kontext}</span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
